import { Button, Input, Label } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/design';
import { mediaApi } from './api/media-api';
import type { IContentCrop, IContentImage } from './api/media-contracts';
import {
  HANDLES,
  type Handle,
  type IPoint,
  type IRect,
  asPercentages,
  isCrop,
  moved,
  pointOf,
  rectBetween,
  resized,
  whole
} from './crop-geometry';
import { FileName, refreshLibrary } from './library';

/**
 * Cropping and resizing an image, in the editing interface and with no external tool — WEB-09
 * AC3, which is the whole of this file's reason to exist.
 *
 * **The browser cuts the picture.** The selection is drawn over the image, re-encoded through a
 * canvas, and uploaded as a *new* library item carrying the image it came from and the rectangle
 * it took. survey-api decodes nothing: it stores the bytes it is given and records the
 * provenance. That is deliberate on both sides — decoding files an editor uploaded is the largest
 * attack surface this feature could have acquired, and the alternative was a server-side imaging
 * library whose licence is revenue-gated.
 *
 * **The original is retained by construction**, not by care: nothing in the system can overwrite
 * an image's bytes, so the source is still there, still its own size, and still refusing to be
 * deleted while anything was cut from it.
 *
 * **The selection is worked with the cursor**: drag the picture to draw one, drag inside it to
 * move it, drag a corner or an edge to resize it. The width field stayed, but it is now the
 * second way rather than the only one — typing a number is how a size is confirmed, not how it
 * is chosen.
 *
 * **One element owns the whole gesture.** The frame takes the pointer down, captures the pointer
 * and handles every move and release itself, so a drag that leaves the frame — past the edge of
 * the picture, which is exactly what cropping to the border means — keeps arriving. The first
 * version listened on `window` instead and depended on the events bubbling there; capture plus
 * handlers on the capturing element is the pattern the platform guarantees.
 *
 * **A GIF comes back a PNG.** A canvas can read one and cannot write one, so the output type
 * falls back where the browser has no encoder — said in the dialog rather than discovered
 * afterwards by someone wondering why the animation stopped.
 */

/** What a canvas can write back. Anything else becomes a PNG. */
const ENCODABLE = ['image/png', 'image/jpeg', 'image/webp'];

/** What the pointer is doing between its press and its release. */
type Gesture =
  | { kind: 'draw'; from: IPoint }
  | { kind: 'move'; from: IPoint; rect: IRect }
  | { kind: 'resize'; handle: Handle };

const CURSOR: Record<Handle, string> = {
  nw: 'cursor-nwse-resize',
  n: 'cursor-ns-resize',
  ne: 'cursor-nesw-resize',
  e: 'cursor-ew-resize',
  se: 'cursor-nwse-resize',
  s: 'cursor-ns-resize',
  sw: 'cursor-nesw-resize',
  w: 'cursor-ew-resize'
};

/** Where a grip sits on the selection's border, as percentages of it. */
const PLACE: Record<Handle, { left: string; top: string }> = {
  nw: { left: '0%', top: '0%' },
  n: { left: '50%', top: '0%' },
  ne: { left: '100%', top: '0%' },
  e: { left: '100%', top: '50%' },
  se: { left: '100%', top: '100%' },
  s: { left: '50%', top: '100%' },
  sw: { left: '0%', top: '100%' },
  w: { left: '0%', top: '50%' }
};

export function CropDialog({
  image,
  onDone
}: {
  image: IContentImage;
  /** Called with the new image when one was made, or with null on cancel. */
  onDone: (cropped: Nullable<IContentImage>) => void;
}) {
  const frame = React.useRef<HTMLDivElement>(null);
  const [selection, setSelection] = React.useState<IRect>(() => whole(image));
  const [gesture, setGesture] = React.useState<Nullable<Gesture>>(null);
  const [outputWidth, setOutputWidth] = React.useState(image.width);
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);

  const type = ENCODABLE.includes(image.contentType) ? image.contentType : 'image/png';
  const reEncoded = type !== image.contentType;
  const cropped = isCrop(selection, image);
  const scale = Math.min(1, outputWidth / selection.width);
  const outputHeight = Math.max(1, Math.round(selection.height * scale));
  // AC3 asks for cropping **or** resizing, so narrowing the whole picture is reason enough to
  // save — a selection alone would disable the button for somebody who only wanted it smaller.
  const changed = cropped || outputWidth < image.width;

  /** The pointer's position in the image's own pixels. */
  const at = (event: React.PointerEvent): IPoint => {
    const bounds = frame.current?.getBoundingClientRect();
    return bounds ? pointOf(bounds, event.clientX, event.clientY, image) : { x: 0, y: 0 };
  };

  const start = (event: React.PointerEvent<HTMLDivElement>, next: Gesture) => {
    // Capture on the frame, which is where the move and release handlers live, so the gesture
    // survives the pointer leaving the picture.
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
    setGesture(next);
  };

  const onMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!gesture) return;
    const point = at(event);
    if (gesture.kind === 'draw') {
      const drawn = rectBetween(gesture.from, point, image);
      if (drawn) {
        setSelection(drawn);
        setOutputWidth(drawn.width);
      }
      return;
    }
    if (gesture.kind === 'move') {
      setSelection(
        moved(gesture.rect, point.x - gesture.from.x, point.y - gesture.from.y, image)
      );
      return;
    }
    const next = resized(selection, gesture.handle, point, image);
    setSelection(next);
    setOutputWidth((width) => Math.min(width, next.width));
  };

  const end = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    setGesture(null);
  };

  const wholeImage = () => {
    setSelection(whole(image));
    setOutputWidth(image.width);
  };

  const cut = async () => {
    setBusy(true);
    setRefusal(null);
    try {
      const file = await render(image.url, selection, outputWidth, outputHeight, type);
      const crop: IContentCrop = {
        x: selection.x,
        y: selection.y,
        width: selection.width,
        height: selection.height
      };
      const { body } = await mediaApi.upload(file, {
        fileName: croppedName(image.fileName, type, cropped),
        kind: image.kind,
        alt: image.alt,
        from: image.imageId,
        crop
      });
      refreshLibrary();
      onDone(body);
    } catch (error) {
      setRefusal(
        error instanceof Error ? error.message : 'The crop could not be saved — try it again.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onDone(null)}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex min-w-0 items-baseline gap-1">
            <span className="shrink-0">Crop</span>
            <FileName name={image.fileName} />
          </DialogTitle>
          <DialogDescription>
            Drag across the picture to choose what to keep, then drag the selection to move it or
            its corners to resize it. The image itself is not changed — the crop is saved as a new
            image beside it.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="max-h-[70dvh] overflow-y-auto">
          <div
            ref={frame}
            onPointerDown={(event) => {
              // A press on the picture itself starts a new selection; the selection and its
              // grips stop the press reaching here and start their own gesture.
              if (event.button !== 0) return;
              start(event, { kind: 'draw', from: at(event) });
            }}
            onPointerMove={onMove}
            onPointerUp={end}
            onPointerCancel={end}
            className="relative touch-none overflow-hidden rounded-md border border-border bg-muted select-none"
          >
            {/* Deliberately NOT `crossOrigin`. Drawing a picture needs no permission, and asking
                for it cost the dialog its picture: the library's tiles fetch this same URL in
                no-cors mode first, blob storage answers them without `Vary: Origin`, and the
                browser then hands that cached copy to a cors-mode request, finds no
                `Access-Control-Allow-Origin` on it and fails the load outright. Reading the
                pixels back is the only step that needs permission, and `render` asks for it
                itself. */}
            <img src={image.url} alt={image.alt} draggable={false} className="block w-full" />

            <div
              role="presentation"
              onPointerDown={(event) => {
                if (event.button !== 0) return;
                event.stopPropagation();
                // Captured on the frame, not here: one element owns the gesture, so a move that
                // leaves this box still arrives.
                frame.current?.setPointerCapture(event.pointerId);
                event.preventDefault();
                setGesture({ kind: 'move', from: at(event), rect: selection });
              }}
              className="absolute cursor-move border-2 border-white"
              style={{
                ...asPercentages(selection, image),
                // What is kept and what is dropped, in one declaration: a spread far larger than
                // any picture darkens everything OUTSIDE this box, and the frame's
                // `overflow-hidden` trims it back to the edge of the image. The first ring is
                // the selection's own outline.
                //
                // This replaced a dim layer with a second copy of the picture laid over it and
                // shifted into place. That worked only while the two agreed about scale, decoded
                // the whole file a second time for a thumbnail-sized hole, and showed nothing at
                // all until the first drag. A shadow has none of those failure modes and cannot
                // disagree with the box it is on.
                boxShadow:
                  '0 0 0 1px var(--color-sapphire), 0 0 0 100vmax '
                  + 'color-mix(in srgb, var(--color-midnight-blue) 55%, transparent)'
              }}
            >

              {HANDLES.map((handle) => (
                <span
                  key={handle}
                  role="presentation"
                  onPointerDown={(event) => {
                    if (event.button !== 0) return;
                    event.stopPropagation();
                    frame.current?.setPointerCapture(event.pointerId);
                    event.preventDefault();
                    setGesture({ kind: 'resize', handle });
                  }}
                  // Large enough to grab with a mouse on a dense picture, centred on its corner
                  // or edge so the grip is where the eye says the border is.
                  className={`absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-[2px] border border-[var(--color-sapphire)] bg-white ${CURSOR[handle]}`}
                  style={PLACE[handle]}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="crop-width">Saved width</Label>
              <Input
                id="crop-width"
                type="number"
                min={1}
                max={selection.width}
                value={outputWidth}
                onChange={(e) =>
                  setOutputWidth(
                    Math.max(1, Math.min(selection.width, Number(e.target.value) || 1))
                  )
                }
                className="w-32"
              />
            </div>
            <p className="min-w-0 flex-1 text-xs text-muted-foreground">
              Keeping {selection.width}×{selection.height} of {image.width}×{image.height}, saved
              at {outputWidth}×{outputHeight}.
              {reEncoded &&
                ` Saved as a PNG — a ${image.contentType.split('/')[1]} cannot be written back by a browser.`}
            </p>
            <Button type="button" variant="ghost" size="sm" onClick={wholeImage}>
              Whole image
            </Button>
          </div>

          {refusal && <p className="text-sm text-[var(--color-deep-magenta)]">{refusal}</p>}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onDone(null)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void cut()} disabled={busy || !changed}>
            {busy ? 'Saving…' : cropped ? 'Save the crop' : 'Save at this size'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** `name.jpg` → `name-cropped.jpg`, with the extension the output really is — and `-resized`
 *  where nothing was cut, because a library of files called "cropped" that were not is a library
 *  nobody can search. */
function croppedName(fileName: string, type: string, cropped: boolean): string {
  const extension = `.${type.split('/')[1]}`.replace('.jpeg', '.jpg');
  const stem = fileName.replace(/\.[^.]+$/, '');
  return `${stem}-${cropped ? 'cropped' : 'resized'}${extension}`;
}

/** What a store with no CORS rule costs, said once and in the words of the thing to change. */
const NO_PERMISSION =
  'The file could not be read for cropping. The image store has to allow this site to read it — '
  + 'a CORS rule for GET, which every environment but a developer\u2019s sets on the storage '
  + 'account.';

/**
 * The selection, re-encoded.
 *
 * **The bytes are fetched here rather than read off the picture on screen**, for two reasons.
 * The displayed element is scaled to its column, so drawing from it would cut the picture at
 * whatever size the window happened to be. And the fetch is the only step that needs permission
 * from the store: it asks in cors mode, past the cache (`reload`), so the no-cors copy the
 * library's tiles left behind cannot answer it — the collision that used to break the dialog.
 *
 * What is drawn is then an **object URL**, which is this page's own origin, so the canvas is
 * never tainted and `toBlob` cannot be refused.
 */
async function render(
  url: string,
  selection: IRect,
  width: number,
  height: number,
  type: string
): Promise<Blob> {
  const response = await fetch(url, { mode: 'cors', cache: 'reload' }).catch(() => null);
  if (!response?.ok) throw new Error(NO_PERMISSION);

  const objectUrl = URL.createObjectURL(await response.blob());
  try {
    const source = await decode(objectUrl);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const surface = canvas.getContext('2d');
    if (!surface) throw new Error('This browser offers no canvas to crop with.');
    surface.drawImage(
      source,
      selection.x,
      selection.y,
      selection.width,
      selection.height,
      0,
      0,
      width,
      height
    );
    return await encode(canvas, type);
  } finally {
    // Released whichever way this ended: an object URL holds the whole file in memory until it
    // is, and a dialog opened a dozen times would hold a dozen.
    URL.revokeObjectURL(objectUrl);
  }
}

function decode(objectUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const source = new Image();
    source.onload = () => resolve(source);
    source.onerror = () =>
      reject(new Error('The file downloaded, but it is not an image this browser can draw.'));
    source.src = objectUrl;
  });
}

function encode(canvas: HTMLCanvasElement, type: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error('The crop could not be encoded — try a smaller selection.')),
      type,
      0.92
    );
  });
}
